import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { EntryContext } from './types'
import { PlayerCard } from './PlayerCard'
import { readPlayerLinkNewTab } from '@/features/_shared/playerLinkPref'

type Launch = (playerId: number, entry?: EntryContext) => void
const LauncherCtx = createContext<Launch | null>(null)

export function usePlayerCardLauncher(): Launch {
  const ctx = useContext(LauncherCtx)
  if (!ctx) throw new Error('usePlayerCardLauncher must be used within PlayerCardProvider')
  return ctx
}

export function usePlayerCardLauncherOptional(): ((playerId: number, entry?: EntryContext) => void) | null {
  return useContext(LauncherCtx)
}

export function PlayerCardProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ id: number; entry: EntryContext } | null>(null)
  const [open, setOpen] = useState(false)
  // This is the single chokepoint every player-card open flows through — the
  // PlayerLink component AND the bespoke name-buttons on the roster / free-agents
  // / players / draft cells all call it. So the "open player links in a new tab"
  // preference is enforced HERE: when on, skip the modal and open the full
  // /player/{id}/view page in a new tab. Because launch runs synchronously inside
  // the click handler, window.open is treated as user-initiated (not popup-blocked).
  // (PlayerLink itself short-circuits before calling launch when the pref is on —
  // its own <a target="_blank"> navigates — so there's never a double-open.)
  const launch = useCallback<Launch>((playerId, entry = 'neutral') => {
    if (readPlayerLinkNewTab()) {
      window.open(`/player/${playerId}/view`, '_blank', 'noopener,noreferrer')
      return
    }
    setState({ id: playerId, entry })
    setOpen(true)
  }, [])
  const value = useMemo(() => launch, [launch])
  return (
    <LauncherCtx.Provider value={value}>
      {children}
      <PlayerCard playerId={state?.id ?? null} entry={state?.entry ?? 'neutral'} open={open} onOpenChange={setOpen} />
    </LauncherCtx.Provider>
  )
}
